import { useEffect, useState } from 'react'
import { cardReferenceMeta } from '../../../api/cardReference'
import { videoInputMeta } from '../../../api/images'
import { billableInputSeconds } from '../../../utils/modelCost'

// Плата за секунды входного видео (выключатель «Правила цены», backend services/input_video_billing): цена на кнопке
// считается по секундам входа, а их меряет сервер — тем же замером, что у списания. Свои ролики — POST /video/input-meta,
// видео тренда — /cards/{id}/reference/video/meta (кэш api/cardReference). Удачный замер помним (новое видео — новая
// ссылка), сбой — нет: следующая смена входа спросит снова.

const measured = new Map<string, number>()
const inflight = new Map<string, Promise<void>>()

function measure(urls: string[]): Promise<void> {
	const todo = urls.filter((u) => !measured.has(u) && !inflight.has(u))
	const waits = urls.map((u) => inflight.get(u)).filter(Boolean) as Promise<void>[]
	if (todo.length) {
		const req = videoInputMeta(todo)
			.then((items) => {
				for (const u of todo) {
					const sec = items.find((i) => i.url === u)?.seconds
					if (typeof sec === 'number' && sec > 0) measured.set(u, sec)
				}
			})
			.catch(() => undefined)
			.finally(() => { for (const u of todo) inflight.delete(u) })
		for (const u of todo) inflight.set(u, req)
		waits.push(req)
	}
	return Promise.all(waits).then(() => undefined)
}

export interface InputVideoSeconds {
	/** Секунды входа к оплате (billableInputSeconds) или null — ещё меряем или не измерилось. */
	seconds: number | null
	/** Меряем — цену со входом пока не показываем, запуск ждёт. */
	pending: boolean
	/** Не измерилось — запускать нельзя: сервер без длины видео цену не посчитает. */
	failed: boolean
}

const OFF: InputVideoSeconds = { seconds: null, pending: false, failed: false }

export function useInputVideoSeconds(
	enabled: boolean,
	ownUrls: string[],
	trendSourceId: string | null,
	/** Секунды входов, известные без замера (UploadedVideo.billedSeconds — отрезок «Повторить движение»). */
	known: number[] = [],
): InputVideoSeconds {
	const key = ownUrls.join('\n')
	const [ownDone, setOwnDone] = useState<string | null>(null)
	const [trend, setTrend] = useState<{ id: string; seconds: number | null } | null>(null)
	useEffect(() => {
		if (!enabled || !key) return
		let alive = true
		void measure(key.split('\n')).then(() => { if (alive) setOwnDone(key) })
		return () => { alive = false }
	}, [enabled, key])
	useEffect(() => {
		if (!enabled || !trendSourceId) return
		let alive = true
		void cardReferenceMeta(trendSourceId).then((meta) => {
			if (alive) setTrend({ id: trendSourceId, seconds: meta?.seconds ?? null })
		})
		return () => { alive = false }
	}, [enabled, trendSourceId])
	if (!enabled || (!ownUrls.length && !trendSourceId && !known.length)) return OFF
	const own = ownUrls.map((u) => measured.get(u))
	const ownPending = own.some((v) => v === undefined) && ownDone !== key
	const trendKnown = !trendSourceId || trend?.id === trendSourceId
	if (ownPending || !trendKnown) return { seconds: null, pending: true, failed: false }
	const seconds = billableInputSeconds([...known, ...own, ...(trendSourceId ? [trend?.seconds ?? undefined] : [])])
	return { seconds, pending: false, failed: seconds == null }
}
